import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Lightbulb, ShieldCheck, HeartHandshake, Sparkles, Coins } from "lucide-react";

const SECTION_ICONS = [HeartHandshake, ShieldCheck, Sparkles, Lightbulb, Coins];

export default function Guidelines() {
  const { t } = useTranslation();
  const nav = useNavigate();
  
  const rawSections = t("guidelines.sections", { returnObjects: true });
  
  let sections = [];
  if (Array.isArray(rawSections)) {
    sections = rawSections;
  } else if (typeof rawSections === "string") {
    try {
      sections = JSON.parse(rawSections);
    } catch {
      try {
        sections = JSON.parse(rawSections.replace(/'/g, '"'));
      } catch {
        sections = [];
      }
    }
  }

  return (
    <div className="min-h-screen bg-[#FFFDF7] text-[#0A0B0D] max-w-2xl mx-auto pb-32" data-testid="guidelines-page">
      {/* Sticky Header */}
      <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-[#E2DFD4]">
        <div className="flex items-center gap-3 px-5 py-4">
          <button
            onClick={() => nav(-1)}
            className="w-10 h-10 -ml-2 flex items-center justify-center rounded-full hover:bg-[#F4F1E8] transition-colors"
            data-testid="guidelines-back"
            aria-label="Back"
          >
            <ArrowLeft className="w-6 h-6 text-[#0A0B0D]" strokeWidth={2.5} />
          </button>
          <div>
            <span className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#14C8B0]">Safety & Trust</span>
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight leading-none">{t("settings.guidelines")}</h1>
          </div>
        </div>
      </div>

      <div className="px-5 pt-6 space-y-6">
        {/* Intro Hero Card */}
        <div className="card-nb p-6 bg-gradient-to-br from-[#14C8B0]/10 via-[#5EEAD4]/10 to-[#FFFDF7] border-2 border-[#121212]">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 rounded-2xl bg-[#14C8B0] text-white flex items-center justify-center font-black text-lg shadow-nb-sm">
              ✨
            </div>
            <h2 className="font-display font-black text-xl">{t("guidelines.promiseTitle", "The TANTA Promise")}</h2>
          </div>
          <p className="text-sm text-[#3D424B] leading-relaxed mb-3">
            {t("guidelines.intro1")}
          </p>
          <p className="text-xs font-display font-extrabold uppercase tracking-wider text-[#0F9D8F] bg-[#DDF5EA] px-3 py-1.5 rounded-full inline-block">
            {t("guidelines.intro2")}
          </p>
        </div>

        {/* Guideline Sections */}
        {sections.map((s, i) => {
          const IconComponent = SECTION_ICONS[i % SECTION_ICONS.length] || Sparkles;
          return (
            <section
              key={i}
              className="card-nb p-6 bg-white border-2 border-[#121212] shadow-soft hover:shadow-soft-md transition-all"
              data-testid={`guidelines-section-${i + 1}`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 rounded-xl bg-[#F7F7F2] border border-[#0A0B0D]/10 flex items-center justify-center text-[#14C8B0] shrink-0 font-display font-black text-sm">
                  <IconComponent className="w-5 h-5" strokeWidth={2.5} />
                </div>
                <h3 className="font-display font-black text-lg sm:text-xl text-[#0A0B0D] tracking-tight">
                  {i + 1}. {s.title}
                </h3>
              </div>

              {s.summary && (
                <p className="text-xs sm:text-sm text-[#4A4F58] font-medium mb-4 pb-3 border-b border-[#EFEEE6]">
                  {s.summary}
                </p>
              )}

              <ul className="space-y-3">
                {(s.points || []).map((p, j) => (
                  <li key={j} className="text-sm text-[#3D424B] pl-4 relative leading-relaxed">
                    <span className="absolute left-0 top-[7px] w-2 h-2 rounded-full bg-[#14C8B0]" aria-hidden="true" />
                    <span className="font-display font-bold text-[#0A0B0D]">{p.lead}:</span> {p.body}
                  </li>
                ))}
              </ul>
            </section>
          );
        })}

        {/* Safety Note Callout */}
        <div className="flex items-start gap-3 bg-gradient-to-r from-[#FFF1D6] to-[#FFE08A] border-2 border-[#121212] rounded-2xl p-5 shadow-nb-sm" data-testid="guidelines-note">
          <Lightbulb className="w-6 h-6 text-[#4A3000] shrink-0 mt-0.5" strokeWidth={2.5} />
          <div>
            <div className="font-display font-black text-sm text-[#4A3000] uppercase tracking-wider mb-1">{t("guidelines.reminderTitle", "Friendly Reminder")}</div>
            <p className="text-sm text-[#4A3000] font-semibold leading-relaxed">{t("guidelines.note")}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
